// src/pages/notifications.jsx — REDESIGNED
import React, { useEffect, useState, useCallback } from 'react';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/layout/Navbar';
import { Spinner } from '../components/ui';
import { notificationApi } from '../services/api';

const TYPE_CONFIG = {
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/15',
    border: 'border-emerald-200 dark:border-emerald-800/40',
    iconBg: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    time: 'text-emerald-600 dark:text-emerald-400',
    icon: 'check_circle',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/15',
    border: 'border-blue-200 dark:border-blue-800/40',
    iconBg: 'bg-blue-500',
    dot: 'bg-blue-500',
    time: 'text-blue-600 dark:text-blue-400',
    icon: 'info',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/15',
    border: 'border-amber-200 dark:border-amber-800/40',
    iconBg: 'bg-amber-500',
    dot: 'bg-amber-500',
    time: 'text-amber-600 dark:text-amber-400',
    icon: 'warning',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-900/15',
    border: 'border-red-200 dark:border-red-800/40',
    iconBg: 'bg-red-500',
    dot: 'bg-red-500',
    time: 'text-red-600 dark:text-red-400',
    icon: 'error',
  },
  delivery: {
    bg: 'bg-sky-50 dark:bg-sky-900/15',
    border: 'border-sky-200 dark:border-sky-800/40',
    iconBg: 'bg-sky-500',
    dot: 'bg-sky-500',
    time: 'text-sky-600 dark:text-sky-400',
    icon: 'local_shipping',
  },
  payment: {
    bg: 'bg-violet-50 dark:bg-violet-900/15',
    border: 'border-violet-200 dark:border-violet-800/40',
    iconBg: 'bg-violet-500',
    dot: 'bg-violet-500',
    time: 'text-violet-600 dark:text-violet-400',
    icon: 'payments',
  },
  system: {
    bg: 'bg-slate-50 dark:bg-slate-800/30',
    border: 'border-slate-200 dark:border-slate-700/50',
    iconBg: 'bg-[#1e9f6b]',
    dot: 'bg-[#1e9f6b]',
    time: 'text-slate-500 dark:text-slate-400',
    icon: 'notifications',
  },
};

const getConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.system;

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  if (diff < 60000) return 'Hozirgina';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} daqiqa oldin`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} soat oldin`;
  return date.toLocaleDateString('uz-UZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadNotifications = useCallback(async () => {
    try {
      const data = await notificationApi.getAll();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useSocketEvent('notification', (payload) => {
    const entry = {
      id: payload?.id || `ws_${Date.now()}`,
      title: payload?.title || 'SmartGaz',
      message: payload?.body || payload?.message || '',
      type: payload?.meta?.type === 'GEOFENCE_ENTER' ? 'info' : 'system',
      isRead: false,
      createdAt: payload?.timestamp || new Date().toISOString(),
    };
    setItems((prev) => [entry, ...prev]);
  });

  const markRead = async (id) => {
    try {
      await notificationApi.markAsRead(id);
    } catch {
      /* silent */
    }
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
  };

  const markAllRead = async () => {
    const unread = items.filter((n) => !n.isRead);
    await Promise.allSettled(unread.map((n) => notificationApi.markAsRead(n.id)));
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const unreadCount = items.filter((n) => !n.isRead).length;
  const displayed = filter === 'unread' ? items.filter((n) => !n.isRead) : items;

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto py-4">
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-12 w-12 bg-[#1e9f6b] rounded-2xl flex items-center justify-center shadow-lg shadow-[#1e9f6b]/25 flex-shrink-0">
              <span
                className="material-symbols-outlined text-white text-[26px]"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                notifications
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight leading-none">
                {t('notifications.title') || 'Xabarlar'}
              </h1>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                {unreadCount > 0 ? (
                  <>
                    <span className="text-[#1e9f6b]">{unreadCount}</span> ta o&apos;qilmagan
                  </>
                ) : (
                  'Barcha bildirishnomalar'
                )}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex items-center justify-between gap-3 mb-6 flex-wrap"
        >
          <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-2xl p-1">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200 ${
                filter === 'all'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              Hammasi
              {items.length > 0 && <span className="ml-1.5 text-[10px] opacity-60">({items.length})</span>}
            </button>
            <button
              type="button"
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5 ${
                filter === 'unread'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              O&apos;qilmagan
              {unreadCount > 0 && (
                <span
                  className={`h-5 min-w-[20px] px-1 rounded-full text-[10px] font-black flex items-center justify-center ${
                    filter === 'unread' ? 'bg-[#1e9f6b] text-white' : 'bg-red-500 text-white'
                  }`}
                >
                  {unreadCount}
                </span>
              )}
            </button>
          </div>

          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-[#1e9f6b] hover:bg-[#1e9f6b]/10 transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                done_all
              </span>
              Barchasini o&apos;qi
            </button>
          )}
        </motion.div>

        {loading ? (
          <div className="py-24 flex justify-center">
            <Spinner size="lg" />
          </div>
        ) : displayed.length === 0 ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="py-20 flex flex-col items-center gap-5 text-center">
              <div className="h-24 w-24 bg-slate-100 dark:bg-slate-800 rounded-[28px] flex items-center justify-center">
                <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-600">
                  notifications_off
                </span>
              </div>
              <div>
                <p className="text-lg font-black italic uppercase tracking-tight text-slate-900 dark:text-white">
                  {filter === 'unread' ? t('notifications.noUnreadItems') : t('notifications.empty')}
                </p>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
                  {t('notifications.emptyHint')}
                </p>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="flex flex-col gap-3">
            <AnimatePresence>
              {displayed.map((n, idx) => {
                const cfg = getConfig(n.type);
                return (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 40, scale: 0.96 }}
                    transition={{ delay: idx * 0.04 }}
                    onClick={() => !n.isRead && markRead(n.id)}
                    className={`
                      relative flex items-start gap-4 p-4 rounded-2xl border
                      transition-all duration-200 cursor-pointer
                      hover:shadow-md hover:scale-[1.01]
                      ${cfg.bg} ${cfg.border}
                      ${n.isRead ? 'opacity-55' : ''}
                    `}
                  >
                    <div className={`h-11 w-11 ${cfg.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <span
                        className="material-symbols-outlined text-white text-[22px]"
                        style={{ fontVariationSettings: "'FILL' 1" }}
                      >
                        {cfg.icon}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-black text-slate-900 dark:text-white leading-snug">
                          {n.title || 'SmartGaz'}
                        </p>
                        {!n.isRead && <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 mt-1 ${cfg.dot}`} />}
                      </div>
                      {n.message && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed line-clamp-2">
                          {n.message}
                        </p>
                      )}
                      <div className={`flex items-center gap-1 mt-2 ${cfg.time}`}>
                        <span className="material-symbols-outlined text-[13px]">schedule</span>
                        <span className="text-[11px] font-bold">{formatTime(n.createdAt)}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
